// The neighbourhood. Names are local labels: the policy has no predicate for a display name
// (see ../../MODEL-REQUESTS.md, R3), so the record itself only ever knows keys.

export type Role = 'resident' | 'steward' | 'triage' | 'community' | 'issuer' | 'newcomer';

export interface CastMember {
  key: string;
  name: { en: string; hi: string };
  g: 'm' | 'f';
  role: Role;
  blurb: { en: string; hi: string };
}

export const CAST: CastMember[] = [
  {
    key: 'ramesh',
    name: { en: 'Ramesh Agarwal', hi: 'रमेश अग्रवाल' },
    g: 'm',
    role: 'resident',
    blurb: { en: 'Runs the hardware shop on Main Road. 52.', hi: 'मेन रोड पर हार्डवेयर की दुकान चलाते हैं। 52 साल।' },
  },
  {
    key: 'ananya',
    name: { en: 'Ananya Iyer', hi: 'अनन्या अय्यर' },
    g: 'f',
    role: 'resident',
    blurb: { en: 'Second-year engineering student. 20.', hi: 'इंजीनियरिंग के दूसरे साल की छात्रा। 20 साल।' },
  },
  {
    key: 'mohan',
    name: { en: 'Mohan Das', hi: 'मोहन दास' },
    g: 'm',
    role: 'resident',
    blurb: { en: 'Retired railway clerk. 67.', hi: 'रेलवे से रिटायर्ड क्लर्क। 67 साल।' },
  },
  {
    key: 'gurpreet',
    name: { en: 'Gurpreet Kaur', hi: 'गुरप्रीत कौर' },
    g: 'f',
    role: 'resident',
    blurb: { en: 'Teaches maths at the government school.', hi: 'सरकारी स्कूल में गणित पढ़ाती हैं।' },
  },
  {
    key: 'farida',
    name: { en: 'Farida Shaikh', hi: 'फ़रीदा शेख़' },
    g: 'f',
    role: 'resident',
    blurb: { en: 'Tailor. Works from home.', hi: 'दर्ज़ी हैं। घर से काम करती हैं।' },
  },
  {
    key: 'sunita',
    name: { en: 'Sunita Yadav', hi: 'सुनीता यादव' },
    g: 'f',
    role: 'resident',
    blurb: { en: 'Grows plants on her terrace.', hi: 'छत पर पौधे उगाती हैं।' },
  },
  {
    key: 'vikram',
    name: { en: 'Vikram Sethi', hi: 'विक्रम सेठी' },
    g: 'm',
    role: 'resident',
    blurb: { en: 'Joined last week. Selling an iPhone.', hi: 'पिछले हफ़्ते जुड़े। iPhone बेच रहे हैं।' },
  },
  {
    key: 'kavita',
    name: { en: 'Kavita Deshpande', hi: 'कविता देशपांडे' },
    g: 'f',
    role: 'steward',
    blurb: { en: 'Steward named in the colony rules. Also a resident.', hi: 'कॉलोनी के नियमों में नामित संचालक। यहीं रहती भी हैं।' },
  },
  {
    key: 'joseph',
    name: { en: 'Joseph Mathew', hi: 'जोसेफ़ मैथ्यू' },
    g: 'm',
    role: 'steward',
    blurb: { en: 'Second steward named in the colony rules. Retired headmaster.', hi: 'कॉलोनी के नियमों में नामित दूसरे संचालक। रिटायर्ड हेडमास्टर।' },
  },
  {
    key: 'triage',
    name: { en: 'Triage agent', hi: 'AI सहायक' },
    g: 'm',
    role: 'triage',
    blurb: { en: 'An AI that sorts reports. It cannot decide.', hi: 'एक AI जो शिकायतों को क्रम में लगाता है। फ़ैसला नहीं कर सकता।' },
  },
  {
    key: 'community',
    name: { en: 'Gulmohar Colony', hi: 'गुलमोहर कॉलोनी' },
    g: 'f',
    role: 'community',
    blurb: { en: 'The community itself. Only it can change the rules.', hi: 'ख़ुद समुदाय। सिर्फ़ यही नियम बदल सकता है।' },
  },
  {
    key: 'phone',
    name: { en: 'Phone check service', hi: 'फ़ोन जाँच सेवा' },
    g: 'f',
    role: 'issuer',
    blurb: { en: 'Checks a number by SMS, signs that it was checked, forgets it.', hi: 'SMS से नंबर जाँचती है, जाँच पर हस्ताक्षर करती है, नंबर भूल जाती है।' },
  },
  {
    key: 'meera',
    name: { en: 'Meera Pillai', hi: 'मीरा पिल्लै' },
    g: 'f',
    role: 'newcomer',
    blurb: { en: 'Moved in last week. Not a member yet.', hi: 'पिछले हफ़्ते यहाँ आईं। अभी सदस्य नहीं।' },
  },
  {
    key: 'imran',
    name: { en: 'Imran Qureshi', hi: 'इमरान क़ुरैशी' },
    g: 'm',
    role: 'newcomer',
    blurb: { en: "Works at Ramesh's shop. Not a member yet.", hi: 'रमेश की दुकान पर काम करते हैं। अभी सदस्य नहीं।' },
  },
];

// The order of seats in the switcher: the people Sahil plays most, first.
export const SEAT_ORDER = ['ananya', 'ramesh', 'farida', 'vikram', 'kavita', 'joseph', 'triage', 'community', 'meera', 'imran', 'mohan', 'gurpreet', 'sunita'];

// Where each resident lives in the colony: a local label, like the names, shown on items so a buyer knows how far to walk.
export const BLOCK: Record<string, { en: string; hi: string }> = {
  ramesh: { en: 'A Block', hi: 'ए ब्लॉक' },
  ananya: { en: 'C Block', hi: 'सी ब्लॉक' },
  mohan: { en: 'B Block', hi: 'बी ब्लॉक' },
  gurpreet: { en: 'D Block', hi: 'डी ब्लॉक' },
  farida: { en: 'B Block', hi: 'बी ब्लॉक' },
  sunita: { en: 'A Block', hi: 'ए ब्लॉक' },
  vikram: { en: 'E Block', hi: 'ई ब्लॉक' },
  kavita: { en: 'C Block', hi: 'सी ब्लॉक' },
  joseph: { en: 'D Block', hi: 'डी ब्लॉक' },
  meera: { en: 'E Block', hi: 'ई ब्लॉक' },
  imran: { en: 'A Block', hi: 'ए ब्लॉक' },
};
